import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HandThumbDownIcon, HandThumbUpIcon } from '@heroicons/react/24/outline';
import InputComponent from '../../components/InputMC';
import ButtonComponent from '../../components/ButtonMC';
import ButtonSelectorMC from '../../components/ButtonSelectorMC';
import { uuid } from '../../../util/utils';
import TitleDescriptionMC from '../../components/TitleDescriptionMC';
import ToggleSwitchMC from '../../components/ToggleSwitchMC';
import ReasonsForClosure from './ReasonsForClosure';

const CreateFunnelStepClosed = ({
  onChange,
  data,
  selectedItem,
  closeAction
}) => {
  const [name, setName] = useState(selectedItem.name || '');
  const [qualification, setQualification] = useState(selectedItem.qualification || 'won');
  const [closureReasonEnabled, setClosureReasonEnabled] = useState(false);
  const [reasonsForClosure, setReasonsForClosure] = useState([]);
  const [customError, setCustomError] = useState(null);
  const isNew = !selectedItem.id;
  const id = useMemo(() => selectedItem.id || uuid(), [selectedItem.id]);

  const { t } = useTranslation('Funnels');

  useEffect(() => {
    if (selectedItem.reasons && selectedItem.reasons.length > 0) {
      setReasonsForClosure(selectedItem.reasons);
      setClosureReasonEnabled(true);
    } else {
      setReasonsForClosure([]);
      setClosureReasonEnabled(false);
    }
  }, [selectedItem]);

  const onSave = () => {
    const allSteps = [...data.openSteps, ...data.closedSteps];
    const nameExists = allSteps.some(
      (step) => step.name === name && step.id !== selectedItem.id
    );

    if (nameExists) {
      setCustomError(t('sidebar.createFromZero.errors.nameExists'));
      return;
    }

    const reasons = reasonsForClosure.filter((r) => r.reason.trim() !== '' && !r._destroy);

    const newItem = {
      id,
      name,
      qualification,
      reasons
    };

    const updatedSteps = isNew
      ? [...data.closedSteps, newItem]
      : data.closedSteps.map((step) => (step.id === selectedItem.id ? newItem : step));
    setReasonsForClosure([]);
    onChange('closedSteps', updatedSteps);
    closeAction();
  };

  const submitDisabled = name.trim().length === 0;

  return (
    <div className="tw-wrapper">
      <TitleDescriptionMC
        title={t('sidebar.createFromZero.stageClosed.fields.name.title')}
        description={isNew ? t('sidebar.createFromZero.stageClosed.fields.name.subtitle') : ''}
        className="tw-mb-2"
      />
      <InputComponent
        value={name}
        onChange={(key, value) => setName(value)}
        name={`step_name_${id}`}
        secondaryLabel={t('sidebar.createFromZero.stageClosed.fields.name.inputLabel')}
        placeholder={t('sidebar.createFromZero.stageClosed.fields.name.placeholder')}
        className="tw-resize-none"
        customError={customError}
        useFloatingAndNonFloatingLabel
      />

      <TitleDescriptionMC
        title={t('sidebar.createFromZero.stageClosed.fields.qualification.title')}
        description={t('sidebar.createFromZero.stageClosed.fields.qualification.subtitle')}
        className="tw-mt-5"
      />
      <div className="tw-flex tw-gap-2 tw-mt-2 tw-mb-3">
        <ButtonSelectorMC
          text={t('sidebar.createFromZero.stageClosed.fields.qualification.won')}
          icon={HandThumbUpIcon}
          isSelected={qualification === 'won'}
          onClick={() => setQualification('won')}
          className="tw-flex-1"
        />
        <ButtonSelectorMC
          text={t('sidebar.createFromZero.stageClosed.fields.qualification.lost')}
          icon={HandThumbDownIcon}
          isSelected={qualification === 'lost'}
          onClick={() => setQualification('lost')}
          className="tw-flex-1"
        />
      </div>
      <ToggleSwitchMC
        isOn={closureReasonEnabled}
        handleToggle={() => {
          if (closureReasonEnabled) {
            // Turning OFF: mark all as _destroy: true
            setReasonsForClosure(reasonsForClosure.map(r => ({ ...r, _destroy: true })));
            setClosureReasonEnabled(false);
          } else {
            // Turning ON: remove _destroy from all
            setReasonsForClosure(reasonsForClosure.map(({ _destroy, ...rest }) => rest));
            setClosureReasonEnabled(true);
          }
        }}
        label={t('sidebar.createFromZero.stageClosed.fields.reasonClosure.label')}
        labelClassName="!tw-font-semibold tw-font-sans"
      />
      <p className="tw-text-xs tw-py-2">
        {t('sidebar.createFromZero.stageClosed.fields.reasonClosure.description')}
      </p>

      {closureReasonEnabled && (
        <ReasonsForClosure
          reasonsForClosure={reasonsForClosure}
          setReasonsForClosure={setReasonsForClosure}
        />
      )}
      <ButtonComponent
        text={
          isNew
            ? t('sidebar.createFromZero.buttons.stepNew')
            : t('sidebar.createFromZero.buttons.stepEdit')
        }
        disabled={submitDisabled}
        className="tw-w-full tw-py-3 tw-mt-6 tw-text-sm"
        onClick={onSave}
      />
      <ButtonComponent
        text={t('sidebar.createFromZero.buttons.cancel')}
        className="tw-w-full tw-py-3 tw-text-sm"
        variant="SECONDARY"
        onClick={closeAction}
      />
    </div>
  );
};

export default CreateFunnelStepClosed;
